import React, { useState, useEffect } from "react";
import ReactTable from "common/components/ReactTable/ReactTable";
import ModalBootstrap from "components/bootstrap/ModalBootstrap";
import { useTranslation } from "react-i18next";
import {
  useGetCarsSummaryQuery,
  useGetReportsByLicenseNumberQuery,
} from "features/api/reports/reportsSlice";
import { FilterFields } from "../Components/types";
import Header from "../Components/Header";
import ExtraDataModal from "../Components/ExtraDataModal";
import { getCarExtraDataTableColumns, getCarsTableColumns } from "../utils";

const CarsReport = () => {
  const { t } = useTranslation();
  const [filterValues, setFilterValues] = useState<FilterFields[]>([]);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageCount, setPageCount] = useState(0);
  const [data, setData] = useState([]);
  const [extraDatafilterValues, setExtraDataFilterValues] = useState<
    FilterFields[]
  >([]);
  const [extraDataPageIndex, setExtraDataPageIndex] = useState(1);
  const [extraDataPageCount, setExtraDataPageCount] = useState(0);
  const [extraData, setExtraData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading] = useState(false);
  const [licenseNumber, setLicenseNumber] = useState("");
  const excelEndpoint = `GetCarsSummaryAsExcelFile`;

  const filterObject = filterValues.reduce(
    (acc, cur) => ({ ...acc, [cur.field]: cur.value }),
    {}
  );
  const filterExtraDataObject = extraDatafilterValues.reduce(
    (acc, cur) => ({ ...acc, [cur.field]: cur.value }),
    {}
  );

  const {
    data: carsData,
    isFetching,
    error,
  } = useGetCarsSummaryQuery({
    page: pageIndex,
    filter: filterObject,
  });

  const { data: extraDataResponse, isFetching: isFetchingExtraData } =
    useGetReportsByLicenseNumberQuery(
      {
        licenseNumber,
        filter: filterExtraDataObject,
        page: extraDataPageIndex,
      },
      {
        skip: !licenseNumber, // Only run the query if licenseNumber is set
      }
    );

  const onCellClick = (carLicenseNumber: string) => {
    setLicenseNumber(carLicenseNumber); // this will caouse the fetch
    setExtraDataPageIndex(1);
    setIsModalOpen(true);
  };

  const columns = getCarsTableColumns(onCellClick);
  const extraDatacolumns = getCarExtraDataTableColumns;

  const submitFilter = (filters: FilterFields[]) => {
    setFilterValues(filters);
  };

  const submitFilterExtraData = (filters: FilterFields[]) => {
    setExtraDataFilterValues(filters);
  };

  useEffect(() => {
    if (!isFetchingExtraData && extraDataResponse) {
      setExtraData(extraDataResponse.objectList || []);
      setExtraDataPageCount(extraDataResponse.totalPages || 0);
    }
  }, [extraDataResponse, isFetchingExtraData]);

  useEffect(() => {
    if (!isFetching && carsData) {
      setData(carsData.objectList || []);
      setPageCount(carsData.totalPages || 0);
    }
  }, [carsData, isFetching, error]);

  return (
    <div style={{ paddingBottom: 20 }}>
      <Header
        columns={columns}
        submitFilter={submitFilter}
        filterValues={filterValues}
        thereIsData={data?.length > 0}
        excelEndpoint={excelEndpoint}
      />
      {isFetching ? (
        <div>Loading...</div>
      ) : (
        <ReactTable
          data={data}
          columns={columns}
          pageIndex={pageIndex}
          setPageIndex={setPageIndex}
          pageCount={pageCount}
        />
      )}
      <ModalBootstrap
        onHide={() => setIsModalOpen(false)}
        show={isModalOpen}
        dialogClassName="extra-data-modal"
        isWithFooter={false}
      >
        {isFetchingExtraData ? (
          <div>Loading extra data...</div>
        ) : (
          <ExtraDataModal
            title={`${t("reportsForCar")}: ${licenseNumber}`}
            titleFor={licenseNumber}
            isLoading={isLoading}
            data={extraData}
            columns={extraDatacolumns}
            pageIndex={extraDataPageIndex}
            setPageIndex={setExtraDataPageIndex}
            pageCount={extraDataPageCount}
            submitFilter={submitFilterExtraData}
            filterValues={extraDatafilterValues}
          />
        )}
      </ModalBootstrap>
    </div>
  );
};

export default CarsReport;
